import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, itemsApi } from '../../utils/api';

interface ImageItem {
  name: string;
  estimatedPrice: number;
  confidence: number;
  condition?: string;
  materials?: string[];
}

interface ImageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: {
    id: string;
    url: string;
    filename: string;
    description?: string;
    capturedAt: string;
    items?: ImageItem[];
    totalEstimate?: number;
    voiceAnnotation?: string;
    itemPhotos?: Array<{
      imageData: string;
      detection: {
        class: string;
        confidence: number;
        boundingBox: {
          x: number;
          y: number;
          width: number;
          height: number;
        };
      };
      voiceAnnotation?: string;
    }>;
  } | null;
  onImageUpdated?: (newImageUrl: string) => void;
}

export const ImageDetailModal: React.FC<ImageDetailModalProps> = ({
  isOpen,
  onClose,
  image,
  onImageUpdated
}) => {
  const navigate = useNavigate();
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [creatingItems, setCreatingItems] = useState<Set<number>>(new Set());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hoveredVoiceIndex, setHoveredVoiceIndex] = useState<number | null>(null);
  const [createdItemIds, setCreatedItemIds] = useState<Map<number, string>>(new Map());
  const [previewItemIndex, setPreviewItemIndex] = useState<number | null>(null);
  
  if (!isOpen || !image) return null;
  
  // Handler to navigate to an existing item
  const handleGoToItem = (itemId: string) => {
    navigate(`/items/${itemId}`);
    onClose();
  };
  
  // Handler to create an item from a detected object
  const handleCreateItemFromDetection = async (itemPhoto: any, index: number) => {
    try {
      setCreatingItems(prev => new Set(prev).add(index));
      console.log('Creating item from detection:', itemPhoto.detection);
      
      // Get AI analysis for this detected object
      let aiEstimation: any = null;
      try {
        // Extract just the base64 part (remove data:image/jpeg;base64, prefix if present)
        let base64Data = itemPhoto.imageData;
        if (base64Data.startsWith('data:')) {
          base64Data = base64Data.split(',')[1];
        }
        
        const aiResponse = await aiApi.estimate({
          base64Image: base64Data, // Send ONLY the base64 string, not the full data URL
          // Don't send detection class - it's often wrong and biases OpenAI's analysis
          // Let OpenAI analyze the image without preconceptions
          description: itemPhoto.voiceAnnotation || undefined
        });
        
        if (aiResponse.data.success) {
          aiEstimation = aiResponse.data.data;
          console.log('AI analysis for detected item:', aiEstimation);
        }
      } catch (aiError) {
        console.warn('AI analysis failed, using detection data only:', aiError);
      }
      
      // Create item data
      // Capitalize the detection class for better presentation
      const capitalizedClass = itemPhoto.detection.class
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Extract MongoDB ID from formatted photo ID (e.g., "photo_12345" -> "12345")
      const photoMongoId = image.id.startsWith('photo_') ? image.id.substring(6) : image.id;
      
      const itemData: any = {
        title: aiEstimation?.recommendedTitle || capitalizedClass,
        description: aiEstimation?.notes || (itemPhoto.voiceAnnotation || `Object detected with ${Math.round(itemPhoto.detection.confidence * 100)}% confidence. Please add description.`),
        images: [itemPhoto.imageData],
        condition: 'used' as const,
        price: aiEstimation?.estimatedValue || 0,
        currency: 'NZD',
        materials: aiEstimation?.attributes || [],
        status: 'draft' as const,
        sourcePhoto: photoMongoId // Link back to parent photo so it won't show as separate photo entry
      };
      
      // Include AI analysis if available
      if (aiEstimation) {
        itemData.aiAnalysis = {
          estimatedValue: aiEstimation.estimatedValue,
          confidence: aiEstimation.confidence,
          notes: aiEstimation.notes,
          recommendedTitle: aiEstimation.recommendedTitle,
          attributes: aiEstimation.attributes,
          reasoning: aiEstimation.reasoning
        };
      }
      
      // Create the item
      const itemResponse = await itemsApi.create(itemData);
      
      if (itemResponse.data.success && itemResponse.data.data) {
        const createdItem = itemResponse.data.data;
        console.log('✅ Item created successfully:', createdItem);
        
        // Store the created item ID for this detection index
        // The API returns either { item: {...} } or the item directly
        const itemId = (createdItem as any).item?._id || (createdItem as any)._id;
        if (itemId) {
          setCreatedItemIds(prev => {
            const newMap = new Map(prev);
            newMap.set(index, itemId);
            return newMap;
          });
        }
        
        setSuccessMessage(`Item "${itemData.title}" created successfully!`);
        setShowSuccessModal(true);
      } else {
        throw new Error('Failed to create item');
      }
      
    } catch (error) {
      console.error('Failed to create item from detection:', error);
      setSuccessMessage('Failed to create item. Please try again.');
      setShowSuccessModal(true);
    } finally {
      setCreatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };
  
  // Handler to create items from all detected objects
  const handleCreateAllItems = async () => {
    const validItemPhotos = (image.itemPhotos || []).filter(item => item.detection);
    
    if (validItemPhotos.length === 0) return;
    
    console.log(`Creating items from ${validItemPhotos.length} detected objects...`);
    
    // Create all items sequentially
    for (let i = 0; i < validItemPhotos.length; i++) {
      await handleCreateItemFromDetection(validItemPhotos[i], i);
    }
    
    console.log('✅ All items created');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRemoveBackground = async () => {
    if (!image || isRemovingBackground) return;
    
    try {
      setIsRemovingBackground(true);
      
      // Call the background removal API
      const response = await aiApi.removeBackground({
        imageUrl: image.url
      });
      
      if (response.data.success && response.data.data?.processedImageUrl) {
        // Notify parent component about the updated image
        if (onImageUpdated) {
          onImageUpdated(response.data.data.processedImageUrl);
        }
        
        // Show success message or update the current image
        // For now, we'll just close the modal - the parent will handle the update
        onClose();
      } else {
        throw new Error('Failed to remove background');
      }
    } catch (error) {
      console.error('Background removal failed:', error);
      alert('Failed to remove background. Please try again.');
    } finally {
      setIsRemovingBackground(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Image Details
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)]">
          {/* Image Section */}
          <div className="lg:w-1/2 p-6 flex items-center justify-center bg-slate-50 dark:bg-slate-900">
            <img
              src={image.url}
              alt={image.filename}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
          </div>

          {/* Details Section */}
          <div className="lg:w-1/2 p-6 overflow-y-auto">
            {/* Basic Info */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                Basic Information
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-400">Filename:</span>
                  <span className="ml-2 text-slate-900 dark:text-slate-100">{image.filename}</span>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-400">Uploaded:</span>
                  <span className="ml-2 text-slate-900 dark:text-slate-100">{formatDate(image.capturedAt)}</span>
                </div>
                {image.description && (
                  <div>
                    <span className="font-medium text-slate-600 dark:text-slate-400">Description:</span>
                    <span className="ml-2 text-slate-900 dark:text-slate-100">{image.description}</span>
                  </div>
                )}
                {image.voiceAnnotation && (
                  <div>
                    <span className="font-medium text-slate-600 dark:text-slate-400">Voice Note:</span>
                    <div className="ml-2 mt-1 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Voice Annotation</span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300 italic">
                        "{image.voiceAnnotation}"
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Total Estimate */}
            {image.totalEstimate && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <h3 className="text-lg font-medium text-green-800 dark:text-green-200 mb-2">
                  Total Estimated Value
                </h3>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {formatPrice(image.totalEstimate)}
                </div>
              </div>
            )}

            {/* Items in Photo */}
            <div className="mb-6">
              {(() => {
                // Calculate valid items count
                const validItemPhotos = (image.itemPhotos || []).filter(item => item.detection);
                const totalValidItems = validItemPhotos.length + (image.items?.length || 0);
                
                // Debug logging
                console.log('ItemPhotos debug:', {
                  totalItemPhotos: image.itemPhotos?.length || 0,
                  validItemPhotos: validItemPhotos.length,
                  items: image.items?.length || 0,
                  itemPhotosData: image.itemPhotos
                });
                
                // Log each item photo individually to see structure
                image.itemPhotos?.forEach((item, i) => {
                  console.log(`ItemPhoto ${i}:`, {
                    hasDetection: !!item.detection,
                    detection: item.detection,
                    hasImageData: !!item.imageData,
                    imageDataLength: item.imageData?.length,
                    allKeys: Object.keys(item),
                    fullObject: item
                  });
                });
                
                return (
                  <>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
                      Items Identified ({totalValidItems})
                    </h3>
                    
                    {/* Show itemPhotos (detected items from camera) */}
                    {validItemPhotos.length > 0 ? (
                      <div className="space-y-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Detected items from this photo:
                          </p>
                          <button
                            onClick={handleCreateAllItems}
                            disabled={creatingItems.size > 0}
                            className="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {creatingItems.size > 0 ? 'Creating...' : 'Create All Items'}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {validItemPhotos.map((itemPhoto, index) => {
                            const hasCreatedItem = createdItemIds.has(index);
                            const itemId = createdItemIds.get(index);
                            
                            return (
                              <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
                                {/* Item Photo - Clickable for preview */}
                                <div 
                                  className="aspect-square bg-slate-100 dark:bg-slate-700 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                                  onClick={() => setPreviewItemIndex(index)}
                                >
                                  <img
                                    src={itemPhoto.imageData}
                                    alt={itemPhoto.detection?.class || 'Detected item'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      console.error('Failed to load item photo:', {
                                        index,
                                        detection: itemPhoto.detection,
                                        imageDataPrefix: itemPhoto.imageData?.substring(0, 50)
                                      });
                                    }}
                                  />
                                </div>
                                
                                {/* Detection Info */}
                                <div className="text-center space-y-2">
                                  <h4 className="font-medium text-slate-900 dark:text-slate-100 capitalize text-sm">
                                    {itemPhoto.detection?.class || 'Unknown'}
                                  </h4>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {Math.round((itemPhoto.detection?.confidence || 0) * 100)}% confidence
                                  </div>
                                  
                                  {/* Voice Annotation */}
                                  {itemPhoto.voiceAnnotation && (
                                    <div className="relative">
                                      <button
                                        className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 cursor-pointer flex items-center justify-center gap-1 mx-auto"
                                        onMouseEnter={() => setHoveredVoiceIndex(index)}
                                        onMouseLeave={() => setHoveredVoiceIndex(null)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setHoveredVoiceIndex(hoveredVoiceIndex === index ? null : index);
                                        }}
                                      >
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                                        </svg>
                                        <span>Voice note</span>
                                      </button>
                                      
                                      {/* Voice annotation tooltip */}
                                      {hoveredVoiceIndex === index && (
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50 w-64 p-3 bg-slate-900 dark:bg-slate-800 text-white rounded-lg shadow-xl border border-slate-700">
                                          <div className="text-xs font-semibold mb-1 text-green-400">Voice Note:</div>
                                          <p className="text-sm leading-relaxed break-words">
                                            {itemPhoto.voiceAnnotation}
                                          </p>
                                          {/* Arrow pointer */}
                                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                                            <div className="border-8 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Create Item or Go to Item Button */}
                                  {hasCreatedItem && itemId ? (
                                    <button
                                      onClick={() => handleGoToItem(itemId)}
                                      className="w-full px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded transition-colors flex items-center justify-center gap-1"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                      </svg>
                                      Go to Item
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleCreateItemFromDetection(itemPhoto, index)}
                                      disabled={creatingItems.has(index)}
                                      className="w-full px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                      {creatingItems.has(index) ? 'Creating...' : 'Create Item'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </>
                );
              })()}
              
              {/* Show legacy items (if any) */}
              {image.items && image.items.length > 0 ? (
                <div className="space-y-4">
                  {image.items.map((item, index) => (
                    <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900 dark:text-slate-100">
                          {item.name}
                        </h4>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {formatPrice(item.estimatedPrice)}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {Math.round(item.confidence * 100)}% confidence
                          </div>
                        </div>
                      </div>
                      
                      {item.condition && (
                        <div className="mb-2">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Condition:</span>
                          <span className="ml-2 text-sm text-slate-900 dark:text-slate-100 capitalize">
                            {item.condition}
                          </span>
                        </div>
                      )}
                      
                      {item.materials && item.materials.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Materials:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {item.materials.map((material, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-full"
                              >
                                {material}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
              
              {/* No items message (only show if BOTH itemPhotos and items are empty) */}
              {(!image.itemPhotos || image.itemPhotos.length === 0) && (!image.items || image.items.length === 0) && (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p>No items identified in this image yet.</p>
                  <p className="text-sm mt-1">AI analysis will be added in a future update.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              {successMessage.includes('Failed') ? (
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              ) : (
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              <h3 className="ml-3 text-lg font-medium text-slate-900 dark:text-slate-100">
                {successMessage.includes('Failed') ? 'Error' : 'Success'}
              </h3>
            </div>
            
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {successMessage}
            </p>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Large Image Preview Modal */}
      {previewItemIndex !== null && image.itemPhotos && image.itemPhotos[previewItemIndex] && (
        <div 
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-90"
          onClick={() => setPreviewItemIndex(null)}
        >
          <div className="relative max-w-4xl w-full">
            {/* Close button */}
            <button
              onClick={() => setPreviewItemIndex(null)}
              className="absolute top-4 right-4 p-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-full transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image */}
            <div className="bg-white dark:bg-slate-800 rounded-lg overflow-hidden">
              <img
                src={image.itemPhotos[previewItemIndex].imageData}
                alt={image.itemPhotos[previewItemIndex].detection?.class || 'Detected item'}
                className="w-full h-auto max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Info overlay */}
              <div className="p-4 bg-gradient-to-t from-black/80 to-transparent absolute bottom-0 left-0 right-0">
                <h3 className="text-2xl font-bold text-white capitalize mb-2">
                  {image.itemPhotos[previewItemIndex].detection?.class || 'Unknown'}
                </h3>
                <p className="text-white/80 text-sm">
                  {Math.round((image.itemPhotos[previewItemIndex].detection?.confidence || 0) * 100)}% confidence
                </p>
                {image.itemPhotos[previewItemIndex].voiceAnnotation && (
                  <div className="mt-3 p-3 bg-white/10 backdrop-blur-sm rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                      </svg>
                      <span className="text-green-400 text-sm font-semibold">Voice Note:</span>
                    </div>
                    <p className="text-white text-base leading-relaxed">
                      {image.itemPhotos[previewItemIndex].voiceAnnotation}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
